import React, { useState, useRef } from "react";
import { toast } from "react-toastify";
import { authFetch } from "../utils/authFetch";
import { BASE_URL } from "../utils/config";
import { useTranslation } from "react-i18next";
import styles from "./BulkUploadModal.module.css";
import * as XLSX from "xlsx";

const BulkUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Parse CSV file
  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter((line) => line.trim());
    if (lines.length === 0) return { headers: [], data: [] };

    // Parse header
    const headerLine = lines[0];
    const parsedHeaders = parseCSVLine(headerLine);

    // Parse data rows
    const dataRows = [];
    for (let i = 1; i < lines.length; i++) {
      const row = parseCSVLine(lines[i]);
      if (row.length > 0) {
        dataRows.push({
          id: i, // Row number for identification
          data: row,
          originalIndex: i - 1, // Original index in CSV (0-based, excluding header)
        });
      }
    }

    return { headers: parsedHeaders, data: dataRows };
  };

  // Parse a single CSV line, handling quoted fields
  const parseCSVLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote
          current += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        // Field separator
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current.trim()); // Add last field

    return result;
  };

  // Handle file selection
  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validate file type
    const validExtensions = [".csv", ".xlsx", ".xls"];
    const fileExtension = selectedFile.name
      .toLowerCase()
      .substring(selectedFile.name.lastIndexOf("."));

    if (!validExtensions.includes(fileExtension)) {
      toast.error(
        t("bulk_upload_invalid_file_type") ||
          "Please select a CSV or Excel file (.csv, .xlsx, .xls)",
      );
      return;
    }

    setFile(selectedFile);

    // Read and parse file
    const reader = new FileReader();
    
    if (fileExtension === ".csv") {
      reader.onload = (event) => {
        try {
          const text = event.target.result;
          const parsed = parseCSV(text);
          setHeaders(parsed.headers);
          setCsvData(parsed.data);
        } catch (error) {
          console.error("Error parsing CSV:", error);
          toast.error(
            t("bulk_upload_parse_error") ||
              "Error parsing file. Please check the file format.",
          );
          setFile(null);
          setCsvData([]);
          setHeaders([]);
        }
      };
      reader.readAsText(selectedFile);
    } else if (fileExtension === ".xlsx" || fileExtension === ".xls") {
      // Handle Excel files
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          
          // Get first sheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Convert to array of arrays
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length === 0) {
            throw new Error("Empty Excel file");
          }
          
          // Extract headers and data
          const parsedHeaders = jsonData[0].map(h => String(h || ""));
          const dataRows = [];
          
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (row && row.length > 0) {
              dataRows.push({
                id: i,
                data: row.map(cell => String(cell || "")),
                originalIndex: i - 1,
              });
            }
          }
          
          setHeaders(parsedHeaders);
          setCsvData(dataRows);
          
        } catch (error) {
          console.error("Error parsing Excel:", error);
          toast.error(
            t("bulk_upload_parse_error") ||
              "Error parsing Excel file. Please check the file format.",
          );
          setFile(null);
          setCsvData([]);
          setHeaders([]);
        }
      };
      reader.readAsArrayBuffer(selectedFile);
    }
  };

  // Remove a row from the preview
  const handleRemoveRow = (rowId) => {
    setCsvData((prev) => prev.filter((row) => row.id !== rowId));
  };

  // Download sample CSV from static file
  const downloadSampleCSV = () => {
    const link = document.createElement("a");
    link.setAttribute("href", "/samples/sample_properties.csv");
    link.setAttribute("download", "sample_properties.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download sample Excel from static file
  const downloadSampleExcel = () => {
    const link = document.createElement("a");
    link.setAttribute("href", "/samples/sample_properties.xlsx");
    link.setAttribute("download", "sample_properties.xlsx");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Convert CSV data back to CSV format for upload
  const convertDataToCSV = () => {
    // Create CSV with headers
    const csvRows = [headers.join(",")];

    // Add data rows
    csvData.forEach((row) => {
      // Ensure row has same number of columns as headers
      const rowData = [];
      headers.forEach((header, index) => {
        const value = row.data[index] || "";
        // Escape commas and quotes in values
        if (
          value.includes(",") ||
          value.includes('"') ||
          value.includes("\n")
        ) {
          rowData.push(`"${value.replace(/"/g, '""')}"`);
        } else {
          rowData.push(value);
        }
      });
      csvRows.push(rowData.join(","));
    });

    return csvRows.join("\n");
  };

  // Handle upload
  const handleUpload = async () => {
    if (csvData.length === 0) {
      toast.error(
        t("bulk_upload_no_data") ||
          "No data to upload. Please add at least one property.",
      );
      return;
    }

    setUploading(true);

    try {
      // Convert filtered data back to CSV
      const csvContent = convertDataToCSV();
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const csvFile = new File([blob], file?.name || "properties.csv", {
        type: "text/csv",
      });

      // Create FormData
      const formData = new FormData();
      formData.append("file", csvFile);

      // Upload to API
      const response = await authFetch(`${BASE_URL}/property/bulk-upload`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(
          t("bulk_upload_success") ||
            `Upload completed! ${data.summary?.successful || 0} successful, ${
              data.summary?.failed || 0
            } failed.`,
        );

        // Show failed rows if any
        if (data.failed && data.failed.length > 0) {
          console.error("Failed rows:", data.failed);
          data.failed.forEach((item) => {
            toast.error(`Row ${item.row}: ${item.error}`, { autoClose: 10000 });
          });
        }

        // Reset state
        setFile(null);
        setCsvData([]);
        setHeaders([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        // Call success callback to refresh properties list
        if (onSuccess) {
          onSuccess();
        }

        // Close modal
        onClose();
      } else {
        toast.error(
          data.error ||
            data.message ||
            t("bulk_upload_error") ||
            "Failed to upload properties. Please try again.",
        );
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(
        t("bulk_upload_network_error") ||
          "Network error while uploading. Please check your connection.",
      );
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {t("bulk_upload_title") || "Bulk Property Upload"}
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Download Sample Files */}
          <div className={styles.sampleSection}>
            <p className={styles.sampleText}>
              {t("bulk_upload_download_sample_text") || "Download a sample file to see the required format:"}
            </p>
            <div className={styles.sampleButtons}>
              <button
                type="button"
                onClick={downloadSampleCSV}
                className={styles.downloadButton}
              >
                {t("bulk_upload_download_sample_csv") || "📥 Download Sample CSV"}
              </button>
              <button
                type="button"
                onClick={downloadSampleExcel}
                className={styles.downloadButtonExcel}
              >
                {t("bulk_upload_download_sample_excel") || "📊 Download Sample Excel"}
              </button>
            </div>
          </div>

          {/* File Upload */}
          <div className={styles.uploadSection}>
            <label className={styles.fileLabel}>
              {t("bulk_upload_select_file") || "Select CSV File"}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                className={styles.fileInput}
              />
            </label>
            {file && (
              <div className={styles.fileInfo}>
                <span>{file.name}</span>
                <button
                  type="button"
                  onClick={() => {
                    setFile(null);
                    setCsvData([]);
                    setHeaders([]);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                  className={styles.removeFileButton}
                >
                  ✕
                </button>
              </div>
            )}
          </div>

          {/* Preview Table */}
          {csvData.length > 0 && (
            <div className={styles.previewSection}>
              <h3 className={styles.previewTitle}>
                {t("bulk_upload_preview") || "Preview"} ({csvData.length}{" "}
                {t("bulk_upload_properties") || "properties"})
              </h3>
              <div className={styles.tableWrapper}>
                <table className={styles.previewTable}>
                  <thead>
                    <tr>
                      <th>{t("bulk_upload_row") || "Row"}</th>
                      {headers.map((header, index) => (
                        <th key={index}>{header}</th>
                      ))}
                      <th>{t("bulk_upload_actions") || "Actions"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvData.map((row, index) => (
                      <tr key={row.id}>
                        <td>{index + 1}</td>
                        {headers.map((header, colIndex) => (
                          <td key={colIndex}>{row.data[colIndex] || ""}</td>
                        ))}
                        <td>
                          <button
                            type="button"
                            onClick={() => handleRemoveRow(row.id)}
                            className={styles.removeRowButton}
                            title={t("bulk_upload_remove_row") || "Remove row"}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Upload Button */}
          {csvData.length > 0 && (
            <div className={styles.uploadButtonSection}>
              <button
                type="button"
                onClick={handleUpload}
                disabled={uploading || csvData.length === 0}
                className={styles.uploadButton}
              >
                {uploading
                  ? t("bulk_upload_uploading") || "Uploading..."
                  : t("bulk_upload_submit") || "Upload Properties"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
